import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import colors from '../../colors';
import typography from '../../typography';
import spacingSystem from '../../spacing';

// Sidebar props
export interface SidebarProps {
  /**
   * The content of the sidebar
   */
  children: React.ReactNode;
  
  /**
   * Whether the sidebar is expanded
   * @default true
   */
  expanded?: boolean;
  
  /**
   * Whether the sidebar is collapsible
   * @default false
   */
  collapsible?: boolean;
  
  /**
   * The width of the sidebar when expanded
   * @default '240px'
   */
  width?: string;
  
  /**
   * The width of the sidebar when collapsed
   * @default '64px'
   */
  collapsedWidth?: string;
  
  /**
   * The position of the sidebar
   * @default 'left'
   */
  position?: 'left' | 'right';
  
  /**
   * The background color of the sidebar
   * @default colors.neutral[100]
   */
  backgroundColor?: string;
  
  /**
   * Callback when the sidebar is expanded or collapsed
   */
  onExpandChange?: (expanded: boolean) => void;
  
  /**
   * Additional class name
   */
  className?: string;
}

// Sidebar section props
export interface SidebarSectionProps {
  /**
   * The title of the section
   */
  title?: React.ReactNode;
  
  /**
   * The content of the section
   */
  children: React.ReactNode;
  
  /**
   * Whether the section is collapsible
   * @default false
   */
  collapsible?: boolean;
  
  /**
   * Whether the section is expanded
   * @default true
   */
  defaultExpanded?: boolean;
  
  /**
   * Additional class name
   */
  className?: string;
}

// Sidebar item props
export interface SidebarItemProps {
  /**
   * The icon of the item
   */
  icon?: React.ReactNode;

  /**
   * The label of the item
   */
  label: React.ReactNode;

  /**
   * Optional description shown below the label
   */
  description?: string;

  /**
   * Whether the item is active
   * @default false
   */
  active?: boolean;

  /**
   * Whether the item is disabled
   * @default false
   */
  disabled?: boolean;

  /**
   * The URL for the item
   */
  href?: string;

  /**
   * Click handler for the item
   */
  onClick?: (event: React.MouseEvent<HTMLAnchorElement | HTMLDivElement>) => void;

  /**
   * Additional class name
   */
  className?: string;
}

// Styled components
const SidebarContainer = styled.aside<{
  expanded: boolean;
  width: string;
  collapsedWidth: string;
  position: 'left' | 'right';
  backgroundColor: string;
}>`
  display: flex;
  flex-direction: column;
  width: ${props => props.expanded ? props.width : props.collapsedWidth};
  height: 100%;
  background-color: ${props => props.backgroundColor};
  transition: width 0.3s ease;
  overflow-x: hidden;
  overflow-y: auto;
  position: relative;
  z-index: 1;
  border-${props => props.position}: 1px solid ${colors.neutral[200]};
`;

const SidebarContent = styled.div<{ expanded: boolean }>`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: ${spacingSystem.spacing[16]};
  
  ${props => !props.expanded && css`
    padding: ${spacingSystem.spacing[8]};
    align-items: center;
  `}
`;

const ToggleButton = styled.button<{ position: 'left' | 'right' }>`
  position: absolute;
  top: ${spacingSystem.spacing[16]};
  ${props => props.position === 'left' ? 'right' : 'left'}: -12px;
  width: 24px;
  height: 24px;
  background-color: ${colors.neutral[100]};
  border: 1px solid ${colors.neutral[200]};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 2;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${colors.neutral[200]};
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${colors.primary[100]};
  }
`;

const ChevronIcon = styled.div<{ expanded: boolean; position: 'left' | 'right' }>`
  width: 12px;
  height: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
  transform: ${props => {
    if (props.position === 'left') {
      return props.expanded ? 'rotate(180deg)' : 'rotate(0deg)';
    } else {
      return props.expanded ? 'rotate(0deg)' : 'rotate(180deg)';
    }
  }};
  
  &::before {
    content: '';
    display: block;
    width: 0;
    height: 0;
    border-top: 4px solid transparent;
    border-bottom: 4px solid transparent;
    border-${props => props.position === 'left' ? 'right' : 'left'}: 6px solid ${colors.neutral[600]};
  }
`;

const SidebarSectionContainer = styled.div<{ expanded: boolean }>`
  margin-bottom: ${spacingSystem.spacing[16]};
  
  &:last-child {
    margin-bottom: 0;
  }
  
  ${props => !props.expanded && css`
    display: flex;
    flex-direction: column;
    align-items: center;
  `}
`;

const SidebarSectionHeader = styled.div<{ expanded: boolean; collapsible: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${props => props.expanded ? 'space-between' : 'center'};
  padding: ${props => props.expanded ? `${spacingSystem.spacing[8]} 0` : '0'};
  margin-bottom: ${props => props.expanded ? spacingSystem.spacing[8] : '0'};
  cursor: ${props => props.collapsible ? 'pointer' : 'default'};
  
  ${props => !props.expanded && css`
    margin-bottom: ${spacingSystem.spacing[8]};
  `}
`;

const SidebarSectionTitle = styled.h3<{ expanded: boolean }>`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  color: ${colors.neutral[500]};
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  ${props => !props.expanded && css`
    display: none;
  `}
`;

const SidebarSectionContent = styled.div<{ expanded: boolean }>`
  display: flex;
  flex-direction: column;
  
  ${props => !props.expanded && css`
    align-items: center;
  `}
`;

const SidebarItemContainer = styled.div<{
  active: boolean;
  disabled: boolean;
  expanded: boolean;
}>`
  display: flex;
  align-items: center;
  padding: ${props => props.expanded ? `${spacingSystem.spacing[8]} ${spacingSystem.spacing[12]}` : spacingSystem.spacing[8]};
  border-radius: 4px;
  margin-bottom: ${spacingSystem.spacing[4]};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  text-decoration: none;
  color: ${props => props.active ? colors.primary[700] : colors.neutral[800]};
  background-color: ${props => props.active ? colors.primary[50] : 'transparent'};
  opacity: ${props => props.disabled ? 0.5 : 1};
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.active ? colors.primary[100] : colors.neutral[100]};
    color: ${props => props.active ? colors.primary[700] : colors.primary[500]};
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SidebarItemIcon = styled.div<{ active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: ${props => props.active ? colors.primary[500] : colors.neutral[600]};
  margin-right: ${spacingSystem.spacing[8]};
`;

const SidebarItemLabelContainer = styled.div<{ expanded: boolean }>`
  display: flex;
  flex-direction: column;
  overflow: hidden;

  ${props => !props.expanded && css`
    display: none;
  `}
`;

const SidebarItemLabel = styled.div`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.md};
  font-weight: ${typography.fontWeight.regular};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SidebarItemDescription = styled.div`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.regular};
  color: ${colors.neutral[500]};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 2px;
`;

// Context for sidebar expanded state
const SidebarContext = React.createContext<boolean>(true);

/**
 * Sidebar component for navigation and actions
 */
export const Sidebar: React.FC<SidebarProps> & {
  Section: React.FC<SidebarSectionProps>;
  Item: React.FC<SidebarItemProps>;
} = ({
  children,
  expanded = true,
  collapsible = false,
  width = '240px',
  collapsedWidth = '64px',
  position = 'left',
  backgroundColor = colors.neutral[100],
  onExpandChange,
  className,
  ...rest
}) => {
  // State for expanded/collapsed
  const [isExpanded, setIsExpanded] = useState(expanded);
  
  // Toggle expanded state
  const toggleExpanded = () => {
    const newExpanded = !isExpanded;
    setIsExpanded(newExpanded);
    
    if (onExpandChange) {
      onExpandChange(newExpanded);
    }
  };
  
  return (
    <SidebarContext.Provider value={isExpanded}>
      <SidebarContainer
        expanded={isExpanded}
        width={width}
        collapsedWidth={collapsedWidth}
        position={position}
        backgroundColor={backgroundColor}
        className={className}
        {...rest}
      >
        {collapsible && (
          <ToggleButton
            onClick={toggleExpanded}
            position={position}
            aria-label={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            <ChevronIcon expanded={isExpanded} position={position} />
          </ToggleButton>
        )}
        <SidebarContent expanded={isExpanded}>
          {children}
        </SidebarContent>
      </SidebarContainer>
    </SidebarContext.Provider>
  );
};

/**
 * SidebarSection component for grouping sidebar items
 */
export const SidebarSection: React.FC<SidebarSectionProps> = ({
  title,
  children,
  collapsible = false,
  defaultExpanded = true,
  className,
  ...rest
}) => {
  // Get expanded state from context
  const expanded = React.useContext(SidebarContext);
  
  // State for section expanded/collapsed
  const [isSectionExpanded, setIsSectionExpanded] = useState(defaultExpanded);
  
  // Toggle section expanded state
  const toggleSectionExpanded = () => {
    if (collapsible) {
      setIsSectionExpanded(!isSectionExpanded);
    }
  };
  
  return (
    <SidebarSectionContainer expanded={expanded} className={className} {...rest}>
      {title && (
        <SidebarSectionHeader
          expanded={expanded}
          collapsible={collapsible}
          onClick={toggleSectionExpanded}
        >
          <SidebarSectionTitle expanded={expanded}>
            {title}
          </SidebarSectionTitle>
          {collapsible && expanded && (
            <ChevronIcon
              expanded={isSectionExpanded}
              position="left"
            />
          )}
        </SidebarSectionHeader>
      )}
      {(!collapsible || isSectionExpanded) && (
        <SidebarSectionContent expanded={expanded}>
          {children}
        </SidebarSectionContent>
      )}
    </SidebarSectionContainer>
  );
};

/**
 * SidebarItem component for individual navigation items
 */
export const SidebarItem: React.FC<SidebarItemProps> = ({
  icon,
  label,
  description,
  active = false,
  disabled = false,
  href,
  onClick,
  className,
  ...rest
}) => {
  // Get expanded state from context
  const expanded = React.useContext(SidebarContext);

  // Handle click
  const handleClick = (event: React.MouseEvent<HTMLAnchorElement | HTMLDivElement>) => {
    if (disabled) {
      event.preventDefault();
      return;
    }

    if (onClick) {
      onClick(event);
    }
  };

  // Common props
  const itemProps = {
    active,
    disabled,
    expanded,
    className,
    onClick: handleClick,
    ...rest,
  };

  // Render as link or div
  const ItemComponent = href && !disabled ? 'a' : 'div';
  const linkProps = href && !disabled ? { href } : {};

  return (
    <SidebarItemContainer
      as={ItemComponent}
      {...itemProps}
      {...linkProps}
    >
      {icon && (
        <SidebarItemIcon active={active}>
          {icon}
        </SidebarItemIcon>
      )}
      <SidebarItemLabelContainer expanded={expanded}>
        <SidebarItemLabel>
          {label}
        </SidebarItemLabel>
        {description && (
          <SidebarItemDescription>
            {description}
          </SidebarItemDescription>
        )}
      </SidebarItemLabelContainer>
    </SidebarItemContainer>
  );
};

// Attach subcomponents
Sidebar.Section = SidebarSection;
Sidebar.Item = SidebarItem;

export default Sidebar;